import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit7Notes: UnitNotes = {
  unitId: "unit-7",
  unitName: "Unit 7: Inference for Quantitative Data – Means",
  examWeight: "10–18%",
  focus: "t-Distributions and Inference for Means",
  sections: [
    {
      id: "topic-7-1",
      title: "Topic 7.1: Introducing Statistics — Why Should I Worry About Error?",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When estimating a population mean (<InlineMath math="\mu" />), we usually rely on the sample mean (<InlineMath math="\bar{x}" />). However, <InlineMath math="\bar{x}" /> varies from sample to sample.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Problem with <InlineMath math="z" /></h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If we knew the population standard deviation (<InlineMath math="\sigma" />), the sampling distribution of <InlineMath math="\bar{x}" /> would be Normal with standard deviation <InlineMath math="\sigma/\sqrt{n}" />. We would calculate:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="z = \frac{\bar{x} - \mu}{\sigma / \sqrt{n}}" />
            </div>
            <ImportantNote>
              In real-world research, if we don't know the population mean <InlineMath math="\mu" />, we almost certainly do not know the population standard deviation <InlineMath math="\sigma" />.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Solution: The Standard Error</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We substitute the sample standard deviation (<InlineMath math="s" />) for <InlineMath math="\sigma" />. This creates the <strong>Standard Error of the Mean (SE)</strong>:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="SE_{\bar{x}} = \frac{s}{\sqrt{n}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Introduction to the <InlineMath math="t" />-Statistic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When we standardize using <InlineMath math="s" /> instead of <InlineMath math="\sigma" />, the statistic is no longer normally distributed. It follows a <strong><InlineMath math="t" />-distribution</strong>.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <BlockMath math="t = \frac{\bar{x} - \mu}{s / \sqrt{n}}" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-2",
      title: "Topic 7.2: Constructing a Confidence Interval for a Population Mean",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The <InlineMath math="t" />-Distribution</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The <InlineMath math="t" />-distribution is similar to the standard Normal distribution but has specific differences:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Shape:</strong> Symmetric and unimodal (bell-shaped), centered at 0.</li>
              <li><strong>Spread:</strong> It has <strong>heavier tails</strong> (more area in the tails) than the Standard Normal curve. This reflects the added uncertainty introduced by estimating <InlineMath math="\sigma" /> with <InlineMath math="s" />.</li>
              <li>
                <strong>Degrees of Freedom (<InlineMath math="df" />):</strong> The shape depends on the sample size: <InlineMath math="df = n - 1" />.
                <p className="text-sm text-slate-600 mt-1">As <InlineMath math="df" /> increases, <InlineMath math="s" /> becomes a better estimate of <InlineMath math="\sigma" />, and the <InlineMath math="t" />-distribution approaches the Standard Normal (<InlineMath math="z" />) distribution.</p>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions for Inference (One-Sample <InlineMath math="t" />)</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Random:</strong> The data must come from a random sample or a randomized experiment.</li>
              <li><strong>10% Condition (Independence):</strong> If sampling without replacement, <InlineMath math="n \le 0.10N" />.</li>
              <li>
                <strong>Normal/Large Sample:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>If <InlineMath math="n \ge 30" />: The CLT applies; the sampling distribution is approximately Normal.</li>
                  <li>If <InlineMath math="n < 30" />: Plot the sample data. If there is <strong>no strong skewness and no outliers</strong>, it is safe to use <InlineMath math="t" />-procedures.</li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The One-Sample <InlineMath math="t" />-Interval Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="\bar{x} \pm t^*_{df} \left( \frac{s}{\sqrt{n}} \right)" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="\bar{x}" />: Sample mean</li>
              <li><InlineMath math="t^*" />: Critical value based on confidence level and <InlineMath math="df = n-1" /> (Found using Table B or calculator <code className="bg-slate-200 px-1 rounded">invT</code>)</li>
              <li><InlineMath math="s" />: Sample standard deviation</li>
              <li><InlineMath math="n" />: Sample size</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-3",
      title: "Topic 7.3: Justifying a Claim About a Population Mean Based on a CI",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Interval</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="font-semibold text-green-900 mb-2">Template:</p>
              <p className="text-slate-700">"We are C% confident that the interval from [lower bound] to [upper bound] captures the true mean [context of the parameter]."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Justifying a Claim</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Confidence intervals are often used to test claims without running a full hypothesis test.
            </p>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Rejecting a Claim</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Scenario:</strong> A company claims the average battery life is 10 hours. You calculate a 95% CI of (8.5, 9.8).</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Justification:</strong> Since the claimed value (10) is <strong>not</strong> contained within the interval, we have evidence to reject the claim at the <InlineMath math="\alpha = 0.05" /> level. The true mean is likely lower.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Supporting a Claim</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Scenario:</strong> A company claims the average battery life is 9 hours. CI is (8.5, 9.8).</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Justification:</strong> Since 9 <strong>is</strong> inside the interval, 9 is a plausible value for the true mean. We do not have evidence to reject the claim.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-4",
      title: "Topic 7.4: Setting Up a Test for a Population Mean",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Hypotheses</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Null Hypothesis (<InlineMath math="H_0" />)</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\mu = \mu_0" /> (The population mean equals a specific hypothesized value).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Alternative Hypothesis (<InlineMath math="H_a" />)</h4>
                <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                  <li><InlineMath math="\mu < \mu_0" /> (one-sided, left-tailed)</li>
                  <li><InlineMath math="\mu > \mu_0" /> (one-sided, right-tailed)</li>
                  <li><InlineMath math="\mu \neq \mu_0" /> (two-sided)</li>
                </ul>
              </div>
            </div>
            <ImportantNote>
              Always define the parameter <InlineMath math="\mu" /> in context (e.g., "where <InlineMath math="\mu" /> is the true mean weight of all apples in the orchard").
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Matched Pairs Design</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes data comes in pairs (e.g., Pre-test vs. Post-test scores for the <em>same</em> student, or left arm vs. right arm).
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Analysis:</strong> We do not analyze two separate means. We calculate the <strong>difference</strong> for each pair first (<InlineMath math="x_{diff} = x_{post} - x_{pre}" />).</li>
              <li><strong>One-Sample Test:</strong> We then perform a <strong>one-sample t-test</strong> on the list of differences.</li>
            </ul>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mt-4">
              <h4 className="font-bold text-purple-900 mb-2">Hypotheses for Paired t-test</h4>
              <ul className="list-disc pl-5 text-sm text-slate-700">
                <li><InlineMath math="H_0: \mu_{diff} = 0" /> (There is no difference on average).</li>
                <li><InlineMath math="H_a: \mu_{diff} > 0" /> (or <InlineMath math="< 0" />, <InlineMath math="\neq 0" />).</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-5",
      title: "Topic 7.5: Carrying Out a Test for a Population Mean",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Calculate the Test Statistic (<InlineMath math="t" />)</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="t = \frac{\bar{x} - \mu_0}{s / \sqrt{n}}" />
            </div>
            <p className="text-slate-700">
              This measures how many standard errors the sample mean is away from the hypothesized mean.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Find the P-value</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Use the <InlineMath math="t" />-distribution with <InlineMath math="df = n-1" />.</li>
              <li><strong>Calculator:</strong> <code className="bg-slate-200 px-1 rounded">tcdf(lower, upper, df)</code> or <code className="bg-slate-200 px-1 rounded">T-Test</code>.</li>
              <li><strong>Table B:</strong> Find the row for <InlineMath math="df" />. Find where your <InlineMath math="t" />-statistic falls. The P-value is the probability in the tail(s).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Conclusion</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>P-value <InlineMath math="< \alpha" />:</strong> Reject <InlineMath math="H_0" />. We have convincing evidence for <InlineMath math="H_a" />.</li>
              <li><strong>P-value <InlineMath math="> \alpha" />:</strong> Fail to reject <InlineMath math="H_0" />. We do not have convincing evidence for <InlineMath math="H_a" />.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-6",
      title: "Topic 7.6: Confidence Intervals for the Difference of Two Means",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This involves two <strong>independent</strong> samples (e.g., mean height of men vs. mean height of women).
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions (Two-Sample)</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Random:</strong> Two independent random samples OR randomized assignment into two treatment groups.</li>
              <li><strong>10% Rule:</strong> Applies to both populations separately (if sampling without replacement).</li>
              <li><strong>Normal/Large Sample:</strong> Both <InlineMath math="n_1 \ge 30" /> and <InlineMath math="n_2 \ge 30" />, OR both populations are Normal, OR graphs of both samples show no strong skew/outliers.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Formula (Two-Sample t-Interval)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="(\bar{x}_1 - \bar{x}_2) \pm t^* \sqrt{\frac{s_1^2}{n_1} + \frac{s_2^2}{n_2}}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Degrees of Freedom (Crucial Distinction)</h3>
            <div className="space-y-3">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Conservative Method (Hand calc)</h4>
                <p className="text-sm text-slate-700 mt-1">Use the smaller of <InlineMath math="n_1 - 1" /> or <InlineMath math="n_2 - 1" />.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Technology (Calculator)</h4>
                <p className="text-sm text-slate-700 mt-1">Uses the Welch-Satterthwaite formula (produces a decimal <InlineMath math="df" />). <strong>This is preferred on the AP Exam</strong> as it is more precise and yields a narrower interval.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-7",
      title: "Topic 7.7: Justifying a Claim About the Difference of Two Means",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Difference</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When interpreting a CI for <InlineMath math="\mu_1 - \mu_2" />:
            </p>
            <div className="space-y-3">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Interval includes 0 (e.g., [-2, 5])</h4>
                <p className="text-sm text-slate-700 mt-1">There is <strong>no convincing evidence</strong> of a difference in means. It is plausible that the means are equal.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Interval entirely positive (e.g., [2, 5])</h4>
                <p className="text-sm text-slate-700 mt-1">We have evidence that <InlineMath math="\mu_1 > \mu_2" />.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">Interval entirely negative (e.g., [-5, -2])</h4>
                <p className="text-sm text-slate-700 mt-1">We have evidence that <InlineMath math="\mu_1 < \mu_2" />.</p>
              </div>
            </div>
          </section>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="font-semibold text-blue-900 mb-2">Phrase Template:</p>
            <p className="text-slate-700">"We are C% confident that the interval [...] captures the true difference in means between [Group 1] and [Group 2]."</p>
          </div>
        </div>
      )
    },
    {
      id: "topic-7-8",
      title: "Topic 7.8: Setting Up a Test for the Difference of Two Population Means",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Hypotheses</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="H_0: \mu_1 - \mu_2 = 0" /> (or <InlineMath math="\mu_1 = \mu_2" />) → The means are the same.</li>
              <li><InlineMath math="H_a: \mu_1 - \mu_2 \neq 0" /> (or <InlineMath math="< 0" />, <InlineMath math="> 0" />) → There is a difference.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pooling?</h3>
            <ImportantNote>
              <strong>Do not pool</strong> variances (<InlineMath math="s_p" />) for means in AP Statistics unless explicitly told that the population variances are equal (which is almost never). On the calculator, select <strong>Pooled: No</strong>.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-9",
      title: "Topic 7.9: Carrying Out a Test for the Difference of Two Population Means",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test Statistic</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="t = \frac{(\bar{x}_1 - \bar{x}_2) - (\mu_1 - \mu_2)_0}{\sqrt{\frac{s_1^2}{n_1} + \frac{s_2^2}{n_2}}}" />
            </div>
            <p className="text-slate-700">
              Usually, the hypothesized difference <InlineMath math="(\mu_1 - \mu_2)_0" /> is 0.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculator Execution</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Use <code className="bg-slate-200 px-1 rounded">2-SampTTest</code>.</li>
              <li>Input data or statistics (<InlineMath math="\bar{x}_1, s_1, n_1" />, etc.).</li>
              <li><strong>Pooled: No</strong>.</li>
              <li>Calculate P-value using the calculator's <InlineMath math="df" />.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Drawing Conclusions</h3>
            <p className="text-slate-700">
              Same logic as one-sample: Compare P-value to <InlineMath math="\alpha" />. State conclusion in terms of the "difference in means."
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-7-10",
      title: "Topic 7.10: Skills Focus — Selecting, Implementing, and Communicating Procedures",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Choosing the Correct Procedure</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Ask these questions:</p>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li>
                <strong>Variable Type?</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Categorical (Yes/No, Success/Fail) → <strong>Proportions (<InlineMath math="z" />)</strong>.</li>
                  <li>Quantitative (Measured values, weights, times) → <strong>Means (<InlineMath math="t" />)</strong>.</li>
                </ul>
              </li>
              <li>
                <strong>Number of Samples?</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>One group of data → One-Sample test/interval.</li>
                  <li>Two groups?
                    <ul className="list-disc pl-6 mt-1">
                      <li>Are they paired (e.g., before/after)? → <strong>Paired <InlineMath math="t" />-test</strong> (One-sample on differences).</li>
                      <li>Are they independent? → <strong>Two-Sample <InlineMath math="t" />-test</strong>.</li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The 4-Step Process</h3>
            <p className="text-slate-700 leading-relaxed mb-4">On the AP Exam free-response section, ensure you include:</p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>State:</strong> Define parameters (<InlineMath math="\mu" />) and state hypotheses (<InlineMath math="H_0, H_a" />) or confidence level.</li>
              <li><strong>Plan:</strong> Name the procedure (e.g., "Two-sample <InlineMath math="t" />-test for difference of means") and check conditions (Random, 10%, Normal/Large Sample).</li>
              <li><strong>Do:</strong> Calculate test statistic (<InlineMath math="t" />), degrees of freedom (<InlineMath math="df" />), and P-value (or the interval). <em>Write down the values provided by the calculator.</em></li>
              <li><strong>Conclude:</strong> Link P-value to <InlineMath math="\alpha" />. Reject or Fail to Reject <InlineMath math="H_0" />. State the conclusion in <strong>context</strong>.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Robustness of <InlineMath math="t" />-procedures</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="t" />-procedures are <strong>robust</strong> against non-Normality of the population when the sample size is large.</li>
              <li>They are <strong>not robust</strong> against outliers. If strong outliers exist in a small sample, <InlineMath math="t" />-procedures should not be used.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "unit-7-summary",
      title: "Summary of Formulas (Unit 7)",
      content: (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-slate-300">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 p-2 text-left">Procedure</th>
                  <th className="border border-slate-300 p-2 text-left">Standard Error (SE)</th>
                  <th className="border border-slate-300 p-2 text-left">Statistic / Formula</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 p-2 font-semibold">One-Sample Mean</td>
                  <td className="border border-slate-300 p-2"><InlineMath math="SE = \frac{s}{\sqrt{n}}" /></td>
                  <td className="border border-slate-300 p-2"><InlineMath math="t = \frac{\bar{x}-\mu_0}{s/\sqrt{n}}" /></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="border border-slate-300 p-2 font-semibold">Matched Pairs Mean</td>
                  <td className="border border-slate-300 p-2"><InlineMath math="SE = \frac{s_{diff}}{\sqrt{n_{diff}}}" /></td>
                  <td className="border border-slate-300 p-2"><InlineMath math="t = \frac{\bar{x}_{diff}-0}{s_{diff}/\sqrt{n_{diff}}}" /></td>
                </tr>
                <tr>
                  <td className="border border-slate-300 p-2 font-semibold">Two-Sample Mean (Diff)</td>
                  <td className="border border-slate-300 p-2"><InlineMath math="SE = \sqrt{\frac{s_1^2}{n_1} + \frac{s_2^2}{n_2}}" /></td>
                  <td className="border border-slate-300 p-2"><InlineMath math="t = \frac{(\bar{x}_1-\bar{x}_2)-0}{\sqrt{\frac{s_1^2}{n_1} + \frac{s_2^2}{n_2}}}" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    }
  ]
};

export default statisticsUnit7Notes;
